import { Pipe, PipeTransform } from '@angular/core';
import { DateTime } from 'luxon';
import { DefaultsService, LocaleService } from '../providers';
import { D } from '@angular/cdk/keycodes';

/**
 * Map of transforms to apply to supplied date format should the format need a specific
 * format for the current locale.
 * These utilize luxon https://moment.github.io/luxon/#/formatting?id=table-of-tokens
 */
const dateFormatModifiers: Record<
  string,
  (locale: string, format?: string) => string
> = {
  'M/d/yyyy, h:mm a (ZZZZ)': (
    locale: string,
    format = 'M/d/yyyy, h:mm a (ZZZZ)'
  ) => {
    let result = 'M/d/yyyy, h:mm a';
    if (['en-GB', 'fr-FR', 'de-DE', 'es-ES', 'it-IT'].includes(locale)) {
      result = 'd/M/yyyy, h:mm a';
    } else if (['ja-JP'].includes(locale)) {
      result = 'yyyy/M/d, h:mm a';
    }
    return result;
  },
};

/**
 * Get the correct date format taking the locale into accoutn
 * @param locale - client locale
 * @param format - a date formatting string.
 *
 * This url shows DatePipe format strings: https://v17.angular.io/api/common/DatePipe
 */
export function getDateFormatForLocale(
  locale: string,
  format: string = 'M/d/yyyy, h:mm a (ZZZZ)'
): string {
  const result = dateFormatModifiers[format]
    ? dateFormatModifiers[format](locale)
    : format;
  return result;
}
/*
 * Formats date for display using the current language
 * Usage:
 *   dateValue | localizedDate
 * Example:
 *   {{ date | rdoLocalizedDate:'MMMM d, y'}}
 *   formats to: Yay
 *   {{ null | rdoLocalizedDate}}
 *   formats to: -
 *
 * Formats used:
 * https://moment.github.io/luxon/#/formatting?id=table-of-tokens
 */
@Pipe({
  name: 'localizedDate',
  pure: false,
  standalone: false,
})
export class LocalizedDatePipe implements PipeTransform {
  private locale: string = 'en-US';
  constructor(private defaultsService?: DefaultsService) {
    // this.locale = this.localeService?.getLocale() || 'en-US';
    this.locale = this.defaultsService?.localeFormat?.LocaleID || 'en-US';
  }

  /**
   * This is used because you can't pass strings to the constructor and use the pipe inline in a template.
   * The string constructor param will throw a very unhelpful error in the DI pipeline. Use this method when
   * building Pipe instances directly.
   * @param locale
   */
  static createInstance(locale?: string) {
    const pipe = new LocalizedDatePipe();
    if (locale) {
      pipe.setLocale(locale);
    }
    return pipe;
  }

  setLocale(locale: string): void {
    this.locale = locale;
  }

  // TODO: add a timezone parameter to allow rezoning of time.
  //  see: https://moment.github.io/luxon/api-docs/index.html#datetimetolocal
  //  https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
  transform(
    value: any,
    format: string = 'M/d/yyyy, h:mm a (ZZZZ)',
    localeOverride?: string,
    timezone?: string
  ): any {
    try {
      const localTime = DateTime.now().toLocal();
      const tz = timezone || localTime.zoneName;

      // the localeFormat that is set up from the server has an incorrect key for Japan
      // it uses 'ja' instead of 'ja-JP'.  Originally we were using the localeService
      // there the locale was also incorrectly returning 'jp-JP'.
      // Leaving them both in place for this hack.
      let loc = localeOverride || this.locale;
      if (loc === 'ja' || loc === 'jp-JP') {
        loc = 'ja-JP';
      }

      const datePattern = getDateFormatForLocale(loc, format);
      const date = DateTime.fromISO(value);

      // For some reason when the (ZZZZ) is part of the format in the dateStr parsing below, it always returns the
      // time offset rather than the timezone abbreviation, but the above 'abbreviation' works. Thus the following
      // hack
      let abbreviation = '';
      if (format === 'M/d/yyyy, h:mm a (ZZZZ)') {
        abbreviation = ` (${date.setZone(tz).toFormat('ZZZZ')})`;
      }

      const dateStr = date
        .setZone(tz)
        // .toLocal()
        .setLocale(loc) // .setZone(tz)
        .toFormat(datePattern)
        .toUpperCase();

      return `${dateStr}${abbreviation}`;
    } catch (error) {
      console.error(error);
    }
  }
}
